import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ValidPhysicalAddressGetResponse } from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { AddressValidationStatus } from '@models/customer';
import { EnteredAddress, ValidAddresses, ValidAddressWithStatus } from '@models/validation';
import { config } from '@services/config';

/* TODO: Fix ValidPhysicalAddressGetResponse type: isValid property type is a string but returns a boolean */
const AddressValidator = {
  NoMatch: (items: ValidAddresses) => items.length === 0,
  PartialMatch: (items: ValidAddresses, isValid: string | boolean) => !isValid && items.length === 1,
  MultipleMatch: (items: ValidAddresses, isValid: string | boolean) => !isValid && items.length,
  ExactMatch: (items: ValidAddresses, isValid: string | boolean) => isValid && items.length,
};

export const useValidPhysicalAddress = (address: EnteredAddress) => {
  const client = useClient();
  const { street1, city, state, zipCode } = address;
  const result = useQuery<ValidPhysicalAddressGetResponse, Error, ValidAddressWithStatus>({
    queryKey: ['validPhysicalAddress', { street1, city, state, zipCode }],
    queryFn: async () =>
      client
        .get(
          `validation/valid-physical-address?street1=${encodeURIComponent(
            street1,
          )}&city=${city}&state=${state}&zipcode=${zipCode}`,
        )
        .json<ValidPhysicalAddressGetResponse>(),
    select: useCallback((data: ValidPhysicalAddressGetResponse) => {
      const { items, isValid } = data;
      if (AddressValidator.NoMatch(items))
        return {
          ...data,
          status: AddressValidationStatus.NoMatch,
        };
      if (AddressValidator.PartialMatch(items, isValid))
        return {
          ...data,
          status: AddressValidationStatus.PartialMatch,
        };
      if (AddressValidator.MultipleMatch(items, isValid))
        return {
          ...data,
          status: AddressValidationStatus.MultipleMatch,
        };
      if (AddressValidator.ExactMatch(items, isValid))
        return {
          ...data,
          status: AddressValidationStatus.ExactMatch,
        };
      return {
        ...data,
        status: AddressValidationStatus.NoMatch,
      };
    }, []),
    enabled: false,
    ...config,
  });
  return {
    ...result,
    validPhysicalAddress: result.data,
    refetchAddress: result.refetch,
  };
};
